import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { COPY } from "../shared/product";
import { api, post } from "./lib/api";

const telemetry = (event_name: string, metadata: Record<string, unknown> = {}) => post("/api/events", { event_name, ...metadata }).catch(() => undefined);
const has = (value: unknown) => value !== undefined && value !== null && value !== "";

type TodayData = {
  item: null | {
    id: string;
    headline: string;
    kind: "prediction" | "move" | "change" | "cross_domain" | "correction" | "pattern" | "evidence" | "observation";
    priority: number;
  };
  poise: null | {
    notice: { kind: string; id: string; text: string; sourceIds: string[] };
    question: string;
    possibleMove: null | { id: string; text: string };
  };
  poise_note: string;
  entitlements: { longitudinalRetrieval?: boolean; recurringPatternMemory?: boolean; evidenceHistory?: boolean; weeklyMirror?: boolean };
};
type ContinuityData = {
  member: boolean;
  membership_status: string;
  server_authoritative: true;
  continuity: null | {
    resume: null | { kind: string; id: string; headline: string; href: string; why: string };
    source_ids: string[];
    loaded_at: string;
  };
};
type SessionData = { authenticated: boolean; user?: { email?: string | null; email_verified_at?: string | null } };

export function TodayV3() {
  const [data, setData] = useState<TodayData | null>(null);
  const [continuity, setContinuity] = useState<ContinuityData | null>(null);
  const [session, setSession] = useState<SessionData | null>(null);
  const [why, setWhy] = useState<Record<string, unknown> | null>(null);
  const [note, setNote] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const checkoutReturn = new URLSearchParams(location.search).get("checkout") === "success";
  const checkoutReported = useRef(false);

  const load = useCallback(async () => {
    void api<ContinuityData>("/api/continuity/return").then(setContinuity).catch(() => undefined);
    void api<SessionData>("/api/auth/session").then(setSession).catch(() => undefined);
    try {
      setData(await api<TodayData>("/api/today"));
    } catch { location.href = "/start"; }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!checkoutReturn || !continuity || checkoutReported.current) return;
    checkoutReported.current = true;
    void telemetry("checkout_return_seen", { membership_confirmed: continuity.member });
  }, [checkoutReturn, continuity]);

  const source = useMemo(() => why?.source as Record<string, unknown> | undefined, [why]);

  async function checkIn(relationship: "better" | "same" | "worse" | "not_related") {
    if (!data?.item || data.item.kind !== "move") return;
    const factual = note.trim();
    if (!factual) { setError("Tell The Mirror what actually happened first."); return; }
    try {
      setError("");
      await post(`/api/moves/${data.item.id}/check-in`, { relationship, note: factual });
      setWhy(null); setNote(""); setMessage("Recorded. Your sentence is the Outcome; the rating stays separate, non-causal Evidence metadata.");
      await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not save this check-in"); }
  }

  function openContinuity() {
    const resume = continuity?.continuity?.resume;
    if (!resume) return;
    void telemetry("continuity_resume_opened", { kind: resume.kind }).finally(() => { location.href = resume.href; });
  }

  const support = Array.isArray(source?.supporting_and_conflicting_statements) ? source!.supporting_and_conflicting_statements as Array<Record<string, unknown>> : [];
  const resume = continuity?.member ? continuity.continuity?.resume : null;
  const claimed = Boolean(session?.user?.email);
  const outcomeReady = note.trim().length > 0;

  return <main className="flow"><a className="wordmark" href="/">WORD &amp; WILL</a><section><p className="eyebrow">TODAY</p>
    {checkoutReturn && <aside data-testid="checkout-return-status">{continuity?.member ? <><strong>Membership confirmed.</strong><p>Your Mirror can continue from your existing private history.</p></> : <><strong>Checkout return received.</strong><p>Continuity activates only after Stripe confirms membership. This page does not grant access from the return URL alone.</p></>}</aside>}
    {resume && <aside data-testid="paid-return-continuity"><p className="eyebrow">CONTINUE WHERE YOU LEFT OFF</p><h1>{resume.headline}</h1><p>{resume.why}</p><button onClick={openContinuity}>Resume this thread</button><p className="fine">This continuation comes from your server-confirmed membership and canonical private record.</p></aside>}
    {data?.item ? <button data-testid="today-remembered-item" className="remembered" onClick={async () => setWhy(await api(`/api/today/${data.item!.id}/why`))}><span>{data.item.kind.replaceAll("_", " ")}</span><strong>{data.item.headline}</strong><small>{COPY.WHY_REMEMBERED}</small></button> : <h1>Your Mirror is ready for the next Catch.</h1>}
    {data?.poise && <aside data-testid="today-poise">
      <p className="eyebrow">ONE QUESTION</p>
      <h2>{data.poise.question}</h2>
      {data.poise.possibleMove && <p data-testid="today-possible-move"><strong>One possible Move:</strong> {data.poise.possibleMove.text}</p>}
      <p className="fine">Today stays narrow on purpose: one thing worth noticing, one question worth asking, and one grounded Move only when your record supports one.</p>
    </aside>}

    {data?.item?.kind === "move" && <aside data-testid="after-check-in"><p className="eyebrow">AFTER</p><h2>What actually happened?</h2><p>State the fact first. The Mirror will not turn Better, Same, or Worse into an invented Outcome.</p><textarea data-testid="after-outcome" required value={note} onChange={event => setNote(event.target.value)} placeholder="One factual sentence about what actually happened"/>{outcomeReady ? <><p><strong>How did that result feel relative to the Move?</strong> This is a separate, non-causal label.</p><div className="actions"><button onClick={() => checkIn("better")}>Better</button><button onClick={() => checkIn("same")}>Same</button><button onClick={() => checkIn("worse")}>Worse</button><button onClick={() => checkIn("not_related")}>Not related</button></div></> : <p className="fine">Write the outcome sentence before choosing a relationship label.</p>}</aside>}

    <a className="button" data-testid="today-catch" href="/catch">Catch me</a>
    {data?.entitlements.longitudinalRetrieval && <a className="button secondary" data-testid="today-current-view" href="/model">What The Mirror currently sees</a>}
    {data?.entitlements.weeklyMirror && <a className="button secondary" href="/weekly">Weekly Mirror</a>}
    {data?.entitlements.evidenceHistory && <a className="button secondary" href="/evidence">Evidence history</a>}

    {!claimed && <aside data-testid="guest-claim-card"><p className="eyebrow">KEEP THIS ACROSS DEVICES</p><h2>Your useful Mirror already happened.</h2><p>This guest record currently follows this browser. Claim the same account if you want recovery and cross-device continuity.</p><a className="button secondary" href="/account">Claim this account</a><p className="fine">Your existing Mirror and private history stay with the same identity.</p></aside>}

    {continuity && !continuity.member && <aside data-testid="continuity-membership-ask"><p className="eyebrow">COME BACK AND I’LL REMEMBER</p><h2>Membership is for continuity, not the first Mirror.</h2><p>Longitudinal memory lets The Mirror connect what you said, what you chose, what actually happened, and what changes next time.</p><a className="button" href="/pricing">See membership</a></aside>}

    <a className="button ghost" href="/voice">Mirror Voice settings</a>
    {message && <p role="status">{message}</p>}{error && <p role="alert">{error}</p>}
    {why && <dialog open data-testid="today-why-remembered"><h2>{COPY.WHY_REMEMBERED}</h2><div data-testid="today-source-evidence">
      {has(source?.will_statement) && <p><strong>Your Will:</strong> {String(source!.will_statement)}</p>}
      {has(source?.headline) && <p><strong>Remembered:</strong> {String(source!.headline)}</p>}
      {has(source?.supporting_statement) && <p><strong>Supporting words:</strong> “{String(source!.supporting_statement)}”</p>}
      {has(source?.chosen_correction) && <p><strong>Your Correction:</strong> “{String(source!.chosen_correction)}”</p>}
      {has(source?.move_text) && <p><strong>Your Move:</strong> {String(source!.move_text)}</p>}
      {has(source?.outcome_description) && <p><strong>Outcome:</strong> {String(source!.outcome_description)}</p>}
      {support.length > 0 && <div><strong>Supporting / conflicting statements:</strong>{support.map((row, index) => <blockquote key={`${String(row.created_at)}-${index}`}>“{String(row.statement ?? "")}” <small>{String(row.created_at ?? "")} · {String(row.support_type ?? "support")}</small></blockquote>)}</div>}
      {has(source?.created_at) && <p><strong>Date:</strong> {String(source!.created_at)}</p>}
      {has(source?.confirmation_state) && <p><strong>Current state:</strong> {String(source!.confirmation_state)}</p>}
      <p className="fine">The Mirror shows why this was remembered through source history and your confirmation state, not a personality score.</p>
    </div><button onClick={() => setWhy(null)}>Close</button></dialog>}
  </section></main>;
}
